declare global {
  interface Navigator {
    brave?: {
      isBrave: () => boolean
    }
  }
}

import Bowser from 'bowser'
export const bowser = Bowser.parse(window.navigator.userAgent)

import type { AppPlatform } from '@gh/common/models'

declare global {
  interface Window {
    beforeinstallpromptEvent: Event | null
  }
}

const isPWADisabled = import.meta.env.VITE_DISABLE_PWA === 'true'

const beforeInstallPromptCallback = (event: Event) => {
  event.preventDefault()
  if (isPWADisabled) {
    return
  }
  window.beforeinstallpromptEvent = event
  // console.log('beforeinstallprompt')
}
const appInstalledCallback = () => {
  // console.log('appinstalled')
}

export function captureBeforeInstallPrompt() {
  window.beforeinstallpromptEvent = null
  window.addEventListener('beforeinstallprompt', beforeInstallPromptCallback)
  window.addEventListener('appinstalled', appInstalledCallback)
}

function getPWADisplayMode() {
  if (document.referrer.startsWith('android-app://')) return 'twa'
  if (window.matchMedia('(display-mode: browser)').matches) return 'browser'
  if (window.matchMedia('(display-mode: standalone)').matches) return 'standalone'
  if (window.matchMedia('(display-mode: minimal-ui)').matches) return 'minimal-ui'
  if (window.matchMedia('(display-mode: fullscreen)').matches) return 'fullscreen'
  if (window.matchMedia('(display-mode: window-controls-overlay)').matches)
    return 'window-controls-overlay'

  return 'unknown'
}

const BROWSER_NAME_CHROME = 'Chrome'
const BROWSER_OS_ANDROID = 'Android'
const BROWSER_OS_IOS = 'iOS'
const BROWSER_OS_MACOS = 'macOS'
const BROWSER_NAME_SAFARI = 'Safari'

const isIPad = (() => {
  return (
    // Checks for iPadOS 13+ (identifies as Mac but has touch)
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
    // Checks for older iPads or specific user agent overrides
    navigator.userAgent.includes('iPad')
  )
})()

const isAndroid = bowser.os.name === BROWSER_OS_ANDROID
const isIOS = bowser.os.name === BROWSER_OS_IOS || (bowser.os.name === BROWSER_OS_MACOS && isIPad)
const isMacOS = bowser.os.name === BROWSER_OS_MACOS && !isIPad
const isApplePlatform = isIOS || isMacOS
const isChrome = bowser.browser.name === BROWSER_NAME_CHROME && !navigator?.brave?.isBrave()
const isSafari = bowser.browser.name === BROWSER_NAME_SAFARI
const isDesktop = bowser.platform.type === 'desktop' && !isIPad

const isAndroidChrome = isAndroid && isChrome
const isAndroidBrowserNotChrome = isAndroid && !isChrome
const isIOSBrowserSafari = isIOS && isSafari
const isIOSBrowserNotSafari = isIOS && !isSafari

const runningInStandaloneMode =
  getPWADisplayMode() === 'standalone' ||
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true

/** App delivery platform: pwa when running standalone, web when in browser. Safe to call at any time. */
export function getAppPlatform(): AppPlatform {
  return runningInStandaloneMode ? 'pwa' : 'web'
}

/**
 * Whether the embedded Zoom player may auto-join after SDK init without an extra tap.
 * Restricted to desktop Chromium (Blink) with a known browser name — relaxed autoplay vs
 * Safari/WebKit/Firefox/Gecko and vs mobile (including Android Chrome, which still hit
 * gesture limits in practice). Unknown or non-Blink engines require manual Join + audio prewarm.
 */
export function isZoomEmbeddedAutoJoinBrowser(): boolean {
  const engineName = bowser.engine?.name
  const browserName = bowser.browser?.name
  if (!isDesktop) return false
  if (engineName !== 'Blink') return false
  if (!browserName || browserName === 'unknown') return false
  if (navigator.brave?.isBrave?.()) return false
  return true
}

export const pwaOnDesktop = import.meta.env.VITE_PWA_ON_DESKTOP === 'true'

const defaultPwaConfig = {
  runningInStandaloneMode,
  displayMode: getPWADisplayMode(),
  // serviceWorkersEnabled: (isAndroidChrome || isIOSBrowserSafari) && runningInStandaloneMode,
  // TODO: Remove this once we enable notifications
  // pwaNotificationsAvailable: (isAndroidChrome || isIOSBrowserSafari) && runningInStandaloneMode,
  // pwaNotificationsAvailable: runningInStandaloneMode,
  pwaNotificationsAvailable: (isAndroidChrome || isIOSBrowserSafari) && runningInStandaloneMode,
  promptChangeBrowserAndroid: isAndroidBrowserNotChrome && !runningInStandaloneMode,
  promptChangeBrowserIOS: isIOSBrowserNotSafari && !runningInStandaloneMode,
  promptSafariInstructionsIOS: isIOS && !runningInStandaloneMode,
  promptInstallOnMobileInstructions: isDesktop,
  supportPwaInstallation: isAndroid && isChrome,
  supportPwaInstallationIOS: isIOSBrowserSafari,
  other: {
    isAndroidChrome,
    isAndroidBrowserNotChrome,
    isIOSBrowserSafari,
    isIOSBrowserNotSafari,
    isIPad,
    isIOS,
    isMacOS,
    isSafari,
    isApplePlatform,
    isDesktop,
    isChrome,
  },
  browserInfo: {
    type: bowser?.platform?.type,
    name: bowser?.browser?.name,
    os: bowser?.os?.name,
    version: bowser?.browser?.version,
    engine: bowser?.engine?.name,
  },
  isDisabled: isPWADisabled,
}

type PwaOnDesktopConfig = typeof defaultPwaConfig

const pwaOnDesktopConfig: Partial<PwaOnDesktopConfig> = {
  pwaNotificationsAvailable: runningInStandaloneMode,
  supportPwaInstallation: (isAndroid || isDesktop) && isChrome,
  promptInstallOnMobileInstructions: isDesktop && !isChrome,
}

export const pwaConfig: PwaOnDesktopConfig = {
  ...defaultPwaConfig,
  ...(pwaOnDesktop ? pwaOnDesktopConfig : {}),
}
